import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Link2, Clock, Palette, Type, Trash2, AlertTriangle, Copy, Eye, EyeOff, Users, Shield, Key, Building2, RefreshCw, PenTool, Plus, X, FileText, Search, ChevronDown, ChevronUp, Pencil } from 'lucide-react';

export default function ConfiguracoesPage() {
  const [activeTab, setActiveTab] = useState('links');
  
  // Links de Acesso
  const [linkForm, setLinkForm] = useState({ perfil: 'usuario', tempo_minutos: 5, nome_visitante: '' });
  const [linksAtivos, setLinksAtivos] = useState([]);
  const [linkGerado, setLinkGerado] = useState(null);
  
  // Senhas das Secretarias
  const [secretarias, setSecretarias] = useState([]);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [senhaForm, setSenhaForm] = useState({ secretaria_id: '', senha_manual: '' });
  const [selectedSecretaria, setSelectedSecretaria] = useState(null);
  
  // Preferências Visuais
  const [preferencias, setPreferencias] = useState({
    cor_primaria: '#003366',
    cor_secundaria: '#0066CC',
    cor_destaque: '#CC0000',
    tamanho_fonte: 'normal'
  });
  
  // Reset
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetForm, setResetForm] = useState({ senha_admin: '', confirmacao: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Assinaturas PDF
  const [assinaturas, setAssinaturas] = useState([
    { nome: 'ANTONIO SOUZA FERREIRA', cargo: 'SEC. ADMINISTRACAO' },
    { nome: 'PEDRO ANTONIO R. AGUIAR', cargo: 'SUB.SECRETARIO' },
    { nome: 'MATHEUS SATURNINO', cargo: 'COORDENADOR' },
  ]);
  const [loadingAssinaturas, setLoadingAssinaturas] = useState(false);

  useEffect(() => {
    fetchLinksAtivos();
    fetchPreferencias();
    fetchSecretarias();
    fetchAssinaturas();
  }, []);

  const fetchLinksAtivos = async () => {
    try {
      const res = await api.get('/config/links-ativos');
      setLinksAtivos(res.data);
    } catch (error) {
      console.error('Erro ao buscar links:', error);
    }
  };

  const fetchSecretarias = async () => {
    try {
      const res = await api.get('/secretarias/listar-senhas');
      setSecretarias(res.data);
    } catch (error) {
      console.error('Erro ao buscar secretarias:', error);
    }
  };

  const fetchPreferencias = async () => {
    try {
      const res = await api.get('/config/preferencias');
      if (res.data) {
        setPreferencias({
          cor_primaria: res.data.cor_primaria || '#003366',
          cor_secundaria: res.data.cor_secundaria || '#0066CC',
          cor_destaque: res.data.cor_destaque || '#CC0000',
          tamanho_fonte: res.data.tamanho_fonte || 'normal'
        });
      }
    } catch (error) {
      console.error('Erro ao buscar preferências:', error);
    }
  };

  const fetchAssinaturas = async () => {
    try {
      const res = await api.get('/config/assinaturas');
      if (res.data && res.data.assinaturas) {
        setAssinaturas(res.data.assinaturas);
      }
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error);
    }
  };

  const handleSaveAssinaturas = async () => {
    const filtered = assinaturas.filter(a => a.nome.trim() && a.cargo.trim());
    if (filtered.length === 0) {
      toast.error('Adicione pelo menos 1 assinante com nome e cargo');
      return;
    }
    if (filtered.length > 4) {
      toast.error('Maximo de 4 assinantes');
      return;
    }
    setLoadingAssinaturas(true);
    try {
      await api.put('/config/assinaturas', { assinaturas: filtered });
      toast.success('Assinaturas salvas com sucesso!');
      setAssinaturas(filtered);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar assinaturas');
    } finally {
      setLoadingAssinaturas(false);
    }
  };

  const addAssinatura = () => {
    if (assinaturas.length >= 4) {
      toast.error('Maximo de 4 assinantes');
      return;
    }
    setAssinaturas([...assinaturas, { nome: '', cargo: '' }]);
  };

  const removeAssinatura = (index) => {
    if (assinaturas.length <= 1) {
      toast.error('Minimo de 1 assinante');
      return;
    }
    setAssinaturas(assinaturas.filter((_, i) => i !== index));
  };

  const updateAssinatura = (index, field, value) => {
    const updated = [...assinaturas];
    updated[index] = { ...updated[index], [field]: value };
    setAssinaturas(updated);
  };

  const handleGerarLink = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/config/gerar-link-acesso', linkForm);
      setLinkGerado(res.data);
      toast.success('Link gerado com sucesso!');
      fetchLinksAtivos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar link');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(window.location.origin + text);
    toast.success('Link copiado!');
  };

  const handleSavePreferencias = async () => {
    try {
      await api.put('/config/preferencias', preferencias);
      toast.success('Preferências salvas!');
      
      // Aplicar preferências no CSS
      document.documentElement.style.setProperty('--cor-primaria', preferencias.cor_primaria);
      document.documentElement.style.setProperty('--cor-secundaria', preferencias.cor_secundaria);
      document.documentElement.style.setProperty('--cor-destaque', preferencias.cor_destaque);
      
      // Aplicar tamanho da fonte
      const fontSizes = { pequeno: '14px', normal: '16px', grande: '18px' };
      document.documentElement.style.fontSize = fontSizes[preferencias.tamanho_fonte];
    } catch (error) {
      toast.error('Erro ao salvar preferências');
    }
  };

  const handleReset = async () => {
    if (resetForm.confirmacao !== 'CONFIRMAR') {
      toast.error('Digite "CONFIRMAR" para prosseguir');
      return;
    }
    
    try {
      const res = await api.post('/config/reset-abastecimentos', resetForm);
      toast.success(res.data.message);
      setResetDialogOpen(false);
      setResetForm({ senha_admin: '', confirmacao: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar reset');
    }
  };

  const getPerfilBadge = (perfil) => {
    const styles = {
      admin: 'bg-red-100 text-red-800',
      supervisor: 'bg-blue-100 text-blue-800',
      usuario: 'bg-green-100 text-green-800',
      secretaria: 'bg-purple-100 text-purple-800'
    };
    return <span className={`px-2 py-1 rounded text-xs font-medium ${styles[perfil]}`}>{perfil}</span>;
  };

  const formatTempo = (segundos) => {
    if (segundos <= 0) return 'Expirado';
    const min = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${min}m ${seg}s`;
  };

  // Funções para senhas das secretarias
  const handleGerarSenha = async (secretariaId, manual = false) => {
    try {
      const data = { 
        secretaria_id: secretariaId,
        senha_manual: manual ? senhaForm.senha_manual : null
      };
      const res = await api.post('/secretarias/gerar-senha', data);
      toast.success(`Senha gerada: ${res.data.senha}`);
      fetchSecretarias();
      setSenhaDialogOpen(false);
      setSenhaForm({ secretaria_id: '', senha_manual: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar senha');
    }
  };

  const copiarSenha = (senha) => {
    navigator.clipboard.writeText(senha);
    toast.success('Senha copiada!');
  };

  const abrirDialogSenha = (secretaria) => {
    setSelectedSecretaria(secretaria);
    setSenhaForm({ secretaria_id: secretaria.id, senha_manual: '' });
    setSenhaDialogOpen(true);
  };

  // ===================== FISCAIS =====================
  const [fiscais, setFiscais] = useState([]);
  const [fiscaisSecretarias, setFiscaisSecretarias] = useState([]);
  const [fiscalDialogOpen, setFiscalDialogOpen] = useState(false);
  const [editingFiscalId, setEditingFiscalId] = useState(null);
  const [fiscalForm, setFiscalForm] = useState({ name: '', login: '', password: '', secretaria_id: '' });
  const [fiscalLancamentos, setFiscalLancamentos] = useState({});
  const [expandedFiscal, setExpandedFiscal] = useState(null);
  const [searchFiscal, setSearchFiscal] = useState('');

  const fetchFiscais = useCallback(async () => {
    try {
      const [fRes, sRes] = await Promise.all([
        api.get('/fiscais'),
        api.get('/secretarias')
      ]);
      setFiscais(fRes.data || []);
      setFiscaisSecretarias(sRes.data || []);
    } catch (e) { console.error('Erro fiscais', e); }
  }, []);

  useEffect(() => {
    if (activeTab === 'fiscais') fetchFiscais();
  }, [activeTab, fetchFiscais]);

  const handleSaveFiscal = async (e) => {
    e.preventDefault();
    if (!fiscalForm.name || !fiscalForm.login) return toast.error('Nome e login obrigatorios');
    if (!editingFiscalId && !fiscalForm.password) return toast.error('Senha obrigatoria');
    try {
      if (editingFiscalId) {
        const payload = { ...fiscalForm };
        if (!payload.password) delete payload.password;
        await api.put(`/fiscais/${editingFiscalId}`, payload);
        toast.success('Fiscal atualizado!');
      } else {
        await api.post('/fiscais', fiscalForm);
        toast.success('Fiscal cadastrado!');
      }
      setFiscalDialogOpen(false);
      setEditingFiscalId(null);
      setFiscalForm({ name: '', login: '', password: '', secretaria_id: '' });
      fetchFiscais();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
  };

  const handleDeleteFiscal = async (id) => {
    if (!window.confirm('Excluir este fiscal?')) return;
    try {
      await api.delete(`/fiscais/${id}`);
      toast.success('Fiscal excluido');
      fetchFiscais();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleEditFiscal = (f) => {
    setFiscalForm({ name: f.name, login: f.login, password: '', secretaria_id: f.secretaria_id || '' });
    setEditingFiscalId(f.id);
    setFiscalDialogOpen(true);
  };

  const toggleFiscalLancamentos = async (fiscalId) => {
    if (expandedFiscal === fiscalId) { setExpandedFiscal(null); return; }
    setExpandedFiscal(fiscalId);
    if (!fiscalLancamentos[fiscalId]) {
      try {
        const res = await api.get(`/fiscais/${fiscalId}/lancamentos`);
        setFiscalLancamentos(prev => ({ ...prev, [fiscalId]: res.data || [] }));
      } catch { toast.error('Erro ao carregar lancamentos'); }
    }
  };

  const handleExportFiscalPdf = async (fiscalId, fiscalName) => {
    try {
      const res = await api.get(`/fiscais/${fiscalId}/lancamentos-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LANCAMENTOS_${fiscalName.replace(/\s/g, '_').toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exportado!');
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  const getSecNome = (secId) => {
    const s = fiscaisSecretarias.find(x => x.id === secId);
    return s ? s.name : '-';
  };

  const fmtDateFiscal = (d) => {
    try { return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const filteredFiscais = fiscais
    .filter(f => !searchFiscal || (f.name || '').toLowerCase().includes(searchFiscal.toLowerCase()) || (f.login || '').toLowerCase().includes(searchFiscal.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      <div>
        <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Configurações</h2>
        <p className="text-slate-600">Gerencie acessos, aparência e dados do sistema</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="links" className="flex items-center gap-1 text-xs">
            <Link2 size={14} /> Links
          </TabsTrigger>
          <TabsTrigger value="fiscais" className="flex items-center gap-1 text-xs" data-testid="tab-fiscais">
            <Shield size={14} /> Fiscais
          </TabsTrigger>
          <TabsTrigger value="senhas" className="flex items-center gap-1 text-xs">
            <Key size={14} /> Senhas
          </TabsTrigger>
          <TabsTrigger value="assinaturas" className="flex items-center gap-1 text-xs" data-testid="tab-assinaturas">
            <PenTool size={14} /> Assinaturas
          </TabsTrigger>
          <TabsTrigger value="aparencia" className="flex items-center gap-1 text-xs">
            <Palette size={14} /> Aparencia
          </TabsTrigger>
          <TabsTrigger value="fonte" className="flex items-center gap-1 text-xs">
            <Type size={14} /> Fonte
          </TabsTrigger>
          <TabsTrigger value="reset" className="flex items-center gap-1 text-xs">
            <Trash2 size={14} /> Reset
          </TabsTrigger>
        </TabsList>

        {/* TAB: Links de Acesso Temporário */}
        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="text-blue-600" />
                Gerar Link de Acesso Temporário
              </CardTitle>
              <CardDescription>
                Crie links com tempo limitado para visitantes acessarem o sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGerarLink} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Nome do Visitante</Label>
                    <Input
                      value={linkForm.nome_visitante}
                      onChange={(e) => setLinkForm({ ...linkForm, nome_visitante: e.target.value })}
                      placeholder="Ex: Auditor, Diretor..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Perfil de Acesso</Label>
                    <select
                      value={linkForm.perfil}
                      onChange={(e) => setLinkForm({ ...linkForm, perfil: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="usuario">Usuário (apenas Dashboard)</option>
                      <option value="supervisor">Supervisor (visualizar tudo)</option>
                      <option value="admin">Administrador (acesso total)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo de Acesso</Label>
                    <select
                      value={linkForm.tempo_minutos}
                      onChange={(e) => setLinkForm({ ...linkForm, tempo_minutos: parseInt(e.target.value) })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(m => (
                        <option key={m} value={m}>{m} minuto{m > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Link2 size={16} className="mr-2" /> Gerar Link
                </Button>
              </form>

              {/* Link Gerado */}
              {linkGerado && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800 mb-2">Link gerado com sucesso!</p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={window.location.origin + linkGerado.url} 
                      readOnly 
                      className="bg-white"
                    />
                    <Button onClick={() => copyToClipboard(linkGerado.url)} variant="outline">
                      <Copy size={16} />
                    </Button>
                  </div>
                  <p className="text-sm text-green-600 mt-2">
                    Perfil: {getPerfilBadge(linkGerado.perfil)} | Expira em: {linkGerado.expira_em}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Links Ativos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="text-orange-600" />
                Links Gerados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {linksAtivos.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhum link gerado ainda</p>
                ) : (
                  linksAtivos.slice(0, 10).map((link, index) => (
                    <div key={index} className={`p-3 rounded-lg border flex justify-between items-center ${link.status === 'expirado' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                      <div>
                        <span className="font-medium">{link.nome_visitante || 'Visitante'}</span>
                        <span className="mx-2">{getPerfilBadge(link.perfil)}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm ${link.status === 'expirado' ? 'text-red-500' : 'text-green-600'}`}>
                          {link.status === 'expirado' ? 'Expirado' : formatTempo(link.tempo_restante)}
                        </span>
                        {link.status !== 'expirado' && (
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(`/acesso-temporario/${link.token}`)}>
                            <Copy size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Fiscais de Abastecimento */}
        <TabsContent value="fiscais" className="space-y-4" data-testid="tab-fiscais-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="text-blue-600" /> Fiscais de Abastecimento
                  </CardTitle>
                  <CardDescription>Cadastre fiscais e visualize seus lancamentos</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <Input placeholder="Buscar fiscal..." value={searchFiscal} onChange={e => setSearchFiscal(e.target.value)} className="pl-8 h-9 w-48 text-xs" data-testid="search-fiscal" />
                  </div>
                  <Button size="sm" onClick={() => { setFiscalForm({ name: '', login: '', password: '', secretaria_id: '' }); setEditingFiscalId(null); setFiscalDialogOpen(true); }} data-testid="add-fiscal-btn">
                    <Plus size={14} className="mr-1" /> NOVO FISCAL
                  </Button>
                </div>
              </div>
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                Link de acesso do fiscal: <span className="font-mono font-bold">{window.location.origin}/fiscal-login</span>
                <Button size="sm" variant="ghost" className="ml-2 h-6 text-xs" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/fiscal-login`); toast.success('Link copiado!'); }}>
                  <Copy size={12} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {filteredFiscais.length === 0 ? (
                <p className="text-center text-gray-400 py-6 text-sm">{searchFiscal ? 'Nenhum fiscal encontrado' : 'Nenhum fiscal cadastrado'}</p>
              ) : (
                <div className="space-y-2">
                  {filteredFiscais.map(f => {
                    const lancs = fiscalLancamentos[f.id] || [];
                    const isExpanded = expandedFiscal === f.id;
                    return (
                      <div key={f.id} className="border rounded-lg overflow-hidden" data-testid={`fiscal-row-${f.id}`}>
                        <div className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer" onClick={() => toggleFiscalLancamentos(f.id)}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#003366] rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {(f.name || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-gray-800">{f.name}</p>
                              <p className="text-[10px] text-gray-500">Login: {f.login} | Secretaria: {getSecNome(f.secretaria_id)}</p>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${f.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                              {(f.status || 'ativo').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleExportFiscalPdf(f.id, f.name); }} data-testid={`export-fiscal-pdf-${f.id}`}>
                              <FileText size={12} className="mr-1" /> PDF
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleEditFiscal(f); }} data-testid={`edit-fiscal-${f.id}`}>
                              <Pencil size={12} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteFiscal(f.id); }} data-testid={`delete-fiscal-${f.id}`}>
                              <Trash2 size={12} />
                            </Button>
                            {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="p-3 border-t bg-white">
                            {lancs.length === 0 ? (
                              <p className="text-center text-gray-400 text-xs py-3">Nenhum lancamento registrado</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead><tr className="bg-[#003366] text-white">
                                    <th className="px-2 py-1.5 text-left rounded-tl-lg">Data/Hora</th>
                                    <th className="px-2 py-1.5 text-left">Fiscal</th>
                                    <th className="px-2 py-1.5 text-left">Motorista</th>
                                    <th className="px-2 py-1.5 text-left">Veiculo</th>
                                    <th className="px-2 py-1.5 text-left">Secretaria</th>
                                    <th className="px-2 py-1.5 text-left">Combustivel</th>
                                    <th className="px-2 py-1.5 text-right">Litros</th>
                                    <th className="px-2 py-1.5 text-left rounded-tr-lg">Posto</th>
                                  </tr></thead>
                                  <tbody>
                                    {lancs.map((l, idx) => (
                                      <tr key={l.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-2 py-1">{fmtDateFiscal(l.data_hora)}</td>
                                        <td className="px-2 py-1 font-medium text-blue-600">{l.fiscal_name}</td>
                                        <td className="px-2 py-1 uppercase">{l.motorista}</td>
                                        <td className="px-2 py-1 uppercase">{l.veiculo || '-'}</td>
                                        <td className="px-2 py-1">{l.secretaria_nome || '-'}</td>
                                        <td className="px-2 py-1">{l.combustivel_tipo}</td>
                                        <td className="px-2 py-1 text-right font-bold text-emerald-600">{parseFloat(l.litros || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1 })} L</td>
                                        <td className="px-2 py-1">{l.posto_nome || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dialog Fiscal */}
          <Dialog open={fiscalDialogOpen} onOpenChange={setFiscalDialogOpen}>
            <DialogContent data-testid="fiscal-dialog">
              <DialogHeader>
                <DialogTitle>{editingFiscalId ? 'Editar Fiscal' : 'Novo Fiscal'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveFiscal} className="space-y-3">
                <div><Label>Nome Completo *</Label><Input value={fiscalForm.name} onChange={e => setFiscalForm(p => ({ ...p, name: e.target.value }))} required data-testid="fiscal-name-input" /></div>
                <div><Label>Login (Usuario) *</Label><Input value={fiscalForm.login} onChange={e => setFiscalForm(p => ({ ...p, login: e.target.value }))} required data-testid="fiscal-login-field" /></div>
                <div><Label>{editingFiscalId ? 'Nova Senha (deixe vazio para manter)' : 'Senha *'}</Label><Input type="password" value={fiscalForm.password} onChange={e => setFiscalForm(p => ({ ...p, password: e.target.value }))} data-testid="fiscal-password-field" /></div>
                <div>
                  <Label>Secretaria Vinculada</Label>
                  <select value={fiscalForm.secretaria_id} onChange={e => setFiscalForm(p => ({ ...p, secretaria_id: e.target.value }))} className="w-full h-10 px-3 rounded-md border text-sm" data-testid="fiscal-secretaria-field">
                    <option value="">Selecione...</option>
                    {fiscaisSecretarias.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="fiscal-save-btn">{editingFiscalId ? 'ATUALIZAR' : 'CADASTRAR'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* TAB: Senhas das Secretarias */}
        <TabsContent value="senhas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="text-purple-600" />
                Gerenciar Senhas das Secretarias
              </CardTitle>
              <CardDescription>
                Gere ou defina senhas para que usuários com perfil "Secretaria" possam acessar seus departamentos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {secretarias.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhuma secretaria cadastrada</p>
                ) : (
                  secretarias.map((sec) => (
                    <div 
                      key={sec.id} 
                      className="p-4 border rounded-lg bg-white hover:shadow-md transition-shadow"
                      data-testid={`secretaria-senha-${sec.id}`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <Building2 className="text-blue-600" size={24} />
                          <div>
                            <p className="font-semibold text-gray-800">{sec.name}</p>
                            <p className="text-sm text-gray-500">
                              {sec.tem_senha ? (
                                <span className="text-green-600 flex items-center gap-1">
                                  <Shield size={14} /> Senha configurada
                                </span>
                              ) : (
                                <span className="text-amber-600 flex items-center gap-1">
                                  <AlertTriangle size={14} /> Sem senha
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {sec.tem_senha && sec.senha_visivel && (
                            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                              <code className="text-sm font-mono text-gray-700">{sec.senha_visivel}</code>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => copiarSenha(sec.senha_visivel)}
                                title="Copiar senha"
                              >
                                <Copy size={14} />
                              </Button>
                            </div>
                          )}
                          
                          <Button 
                            onClick={() => abrirDialogSenha(sec)}
                            variant={sec.tem_senha ? "outline" : "default"}
                            className={!sec.tem_senha ? "bg-purple-600 hover:bg-purple-700" : ""}
                            data-testid={`gerar-senha-btn-${sec.id}`}
                          >
                            <RefreshCw size={16} className="mr-2" />
                            {sec.tem_senha ? 'Alterar Senha' : 'Gerar Senha'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Instruções */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Users size={18} />
                Como funciona o acesso por Secretaria?
              </h4>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>1. Crie um usuário com o perfil <strong>"Secretaria"</strong> na página de Usuários</li>
                <li>2. Gere uma senha para a secretaria que este usuário poderá acessar</li>
                <li>3. Compartilhe a senha com o responsável pela secretaria</li>
                <li>4. Ao entrar, o usuário verá apenas a página "Secretarias" e precisará digitar a senha para acessar seu departamento</li>
                <li>5. Com acesso liberado, poderá gerenciar funcionários e veículos do seu departamento</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Assinaturas PDF */}
        <TabsContent value="assinaturas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PenTool className="text-blue-600" />
                Assinaturas do Relatório PDF
              </CardTitle>
              <CardDescription>
                Configure quem assina o relatório geral (de 1 a 4 pessoas)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {assinaturas.map((a, index) => (
                <div key={index} className="flex items-end gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid={`assinatura-row-${index}`}>
                  <div className="flex-1 space-y-2">
                    <Label className="text-xs font-semibold text-slate-600">Nome do Responsável {index + 1}</Label>
                    <Input
                      value={a.nome}
                      onChange={(e) => updateAssinatura(index, 'nome', e.target.value)}
                      placeholder="Ex: ANTONIO SOUZA FERREIRA"
                      className="font-medium uppercase"
                      data-testid={`assinatura-nome-${index}`}
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label className="text-xs font-semibold text-slate-600">Cargo</Label>
                    <Input
                      value={a.cargo}
                      onChange={(e) => updateAssinatura(index, 'cargo', e.target.value)}
                      placeholder="Ex: SEC. ADMINISTRACAO"
                      className="uppercase"
                      data-testid={`assinatura-cargo-${index}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeAssinatura(index)}
                    className="h-10 w-10 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    disabled={assinaturas.length <= 1}
                    data-testid={`remove-assinatura-${index}`}
                  >
                    <X size={18} />
                  </Button>
                </div>
              ))}

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={addAssinatura}
                  disabled={assinaturas.length >= 4}
                  className="flex items-center gap-2"
                  data-testid="add-assinatura-btn"
                >
                  <Plus size={16} /> Adicionar Assinante ({assinaturas.length}/4)
                </Button>

                <Button
                  onClick={handleSaveAssinaturas}
                  disabled={loadingAssinaturas}
                  className="bg-[#003366] hover:bg-[#002244] text-white"
                  data-testid="save-assinaturas-btn"
                >
                  {loadingAssinaturas ? 'Salvando...' : 'Salvar Assinaturas'}
                </Button>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 border-t pt-6">
                <p className="text-xs text-slate-500 mb-3 font-semibold uppercase tracking-wider">Pré-visualização no PDF:</p>
                <div className="flex justify-center gap-8 flex-wrap">
                  {assinaturas.filter(a => a.nome.trim() && a.cargo.trim()).map((a, i) => (
                    <div key={i} className="text-center min-w-[150px]">
                      <div className="border-b border-slate-400 mb-1 w-full"></div>
                      <p className="text-xs font-bold text-slate-800">{a.nome.toUpperCase()}</p>
                      <p className="text-xs text-slate-600">{a.cargo.toUpperCase()}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Aparência (Cores) */}
        <TabsContent value="aparencia">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="text-purple-600" />
                Paleta de Cores
              </CardTitle>
              <CardDescription>
                Personalize as cores do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Cor Primária</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={preferencias.cor_primaria}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_primaria: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={preferencias.cor_primaria}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_primaria: e.target.value })}
                      className="w-28"
                    />
                  </div>
                  <div className="w-full h-8 rounded" style={{ backgroundColor: preferencias.cor_primaria }}></div>
                </div>
                
                <div className="space-y-2">
                  <Label>Cor Secundária</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={preferencias.cor_secundaria}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_secundaria: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={preferencias.cor_secundaria}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_secundaria: e.target.value })}
                      className="w-28"
                    />
                  </div>
                  <div className="w-full h-8 rounded" style={{ backgroundColor: preferencias.cor_secundaria }}></div>
                </div>
                
                <div className="space-y-2">
                  <Label>Cor de Destaque</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={preferencias.cor_destaque}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_destaque: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={preferencias.cor_destaque}
                      onChange={(e) => setPreferencias({ ...preferencias, cor_destaque: e.target.value })}
                      className="w-28"
                    />
                  </div>
                  <div className="w-full h-8 rounded" style={{ backgroundColor: preferencias.cor_destaque }}></div>
                </div>
              </div>

              {/* Preview */}
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <div className="flex gap-4">
                  <Button style={{ backgroundColor: preferencias.cor_primaria }}>Botão Primário</Button>
                  <Button style={{ backgroundColor: preferencias.cor_secundaria }}>Botão Secundário</Button>
                  <Button style={{ backgroundColor: preferencias.cor_destaque }}>Botão Destaque</Button>
                </div>
              </div>

              <Button onClick={handleSavePreferencias} className="bg-green-600 hover:bg-green-700">
                Salvar Cores
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Tamanho da Fonte */}
        <TabsContent value="fonte">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="text-indigo-600" />
                Tamanho da Fonte
              </CardTitle>
              <CardDescription>
                Ajuste o tamanho do texto em todo o sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4">
                {['pequeno', 'normal', 'grande'].map((tamanho) => (
                  <button
                    key={tamanho}
                    onClick={() => setPreferencias({ ...preferencias, tamanho_fonte: tamanho })}
                    className={`flex-1 p-6 rounded-lg border-2 transition-all ${
                      preferencias.tamanho_fonte === tamanho 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className={`font-medium ${
                      tamanho === 'pequeno' ? 'text-sm' : tamanho === 'grande' ? 'text-xl' : 'text-base'
                    }`}>
                      {tamanho.charAt(0).toUpperCase() + tamanho.slice(1)}
                    </p>
                    <p className={`text-gray-500 mt-1 ${
                      tamanho === 'pequeno' ? 'text-xs' : tamanho === 'grande' ? 'text-lg' : 'text-sm'
                    }`}>
                      Exemplo de texto
                    </p>
                  </button>
                ))}
              </div>

              <Button onClick={handleSavePreferencias} className="bg-green-600 hover:bg-green-700">
                Salvar Tamanho
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Reset */}
        <TabsContent value="reset">
          <Card className="border-red-200">
            <CardHeader className="bg-red-50 rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle />
                Zona de Perigo
              </CardTitle>
              <CardDescription className="text-red-600">
                Ações irreversíveis que afetam todos os dados do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <h4 className="font-semibold text-red-800 mb-2">Reset de Abastecimentos</h4>
                <p className="text-sm text-red-600 mb-4">
                  Esta ação irá apagar <strong>TODOS</strong> os registros de abastecimento do sistema. 
                  Um backup será criado automaticamente antes da exclusão.
                </p>
                <Button 
                  variant="destructive" 
                  onClick={() => setResetDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 size={16} className="mr-2" />
                  Realizar Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de Confirmação do Reset */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle />
              Confirmar Reset de Abastecimentos
            </DialogTitle>
            <DialogDescription>
              Esta ação é <strong>IRREVERSÍVEL</strong>. Todos os abastecimentos serão apagados.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <strong>ATENÇÃO:</strong> Um backup será criado antes da exclusão, mas os dados 
                não poderão ser restaurados automaticamente.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Digite sua senha de administrador:</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={resetForm.senha_admin}
                  onChange={(e) => setResetForm({ ...resetForm, senha_admin: e.target.value })}
                  placeholder="Sua senha..."
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Digite <strong>CONFIRMAR</strong> para prosseguir:</Label>
              <Input
                value={resetForm.confirmacao}
                onChange={(e) => setResetForm({ ...resetForm, confirmacao: e.target.value.toUpperCase() })}
                placeholder="CONFIRMAR"
                className={resetForm.confirmacao === 'CONFIRMAR' ? 'border-green-500' : ''}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReset}
              disabled={resetForm.confirmacao !== 'CONFIRMAR' || !resetForm.senha_admin}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 size={16} className="mr-2" />
              Confirmar Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Gerar/Alterar Senha da Secretaria */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Key />
              {selectedSecretaria?.tem_senha ? 'Alterar' : 'Gerar'} Senha - {selectedSecretaria?.name}
            </DialogTitle>
            <DialogDescription>
              Defina uma senha manual ou gere uma automática para esta secretaria
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Senha (deixe em branco para gerar automaticamente):</Label>
              <Input
                type="text"
                value={senhaForm.senha_manual}
                onChange={(e) => setSenhaForm({ ...senhaForm, senha_manual: e.target.value })}
                placeholder="Digite uma senha ou deixe vazio..."
              />
              <p className="text-xs text-gray-500">
                Se deixar em branco, será gerada uma senha no formato: SEC-NOME-XXXX
              </p>
            </div>
            
            {selectedSecretaria?.tem_senha && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>Atenção:</strong> Esta secretaria já possui uma senha. Ao gerar uma nova, a senha anterior será substituída.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSenhaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleGerarSenha(selectedSecretaria?.id, !!senhaForm.senha_manual)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Key size={16} className="mr-2" />
              {senhaForm.senha_manual ? 'Definir Senha' : 'Gerar Senha Automática'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
